import { useEffect, useRef, useState } from 'react';
import type { AnswerMap, ClientIntakePayload, JsonValue } from '../shared/types';
import { FIRM_NAME, FIRM_OFFICE_PHONES } from '../shared/firm';
import { api } from './api';
import { revealFeedback } from './feedback';
import { QuestionnaireEditor } from './QuestionnaireEditor';
import { friendlySaveError } from './saveFeedback';

export function ClientApp() {
	const [intake, setIntake] = useState<ClientIntakePayload | null>(null);
	const [answers, setAnswers] = useState<AnswerMap>({});
	const [message, setMessage] = useState('Loading your questionnaire...');
	const [messageKind, setMessageKind] = useState<'status' | 'error' | 'confirmation'>('status');
	const [largeText, setLargeText] = useState(() => {
		try {
			return window.localStorage.getItem('bz-large-text') === 'true';
		} catch {
			return false;
		}
	});
	const saveChain = useRef<Promise<void>>(Promise.resolve());
	const versionRef = useRef(1);
	const messageRef = useRef<HTMLParagraphElement>(null);

	useEffect(() => {
		void api<ClientIntakePayload>('/api/client/intake')
			.then((payload) => {
				setIntake(payload);
				setAnswers(payload.answers);
				versionRef.current = payload.rowVersion;
				setMessageKind('status');
				setMessage('All changes save automatically.');
			})
			.catch((error: Error) => {
				setMessageKind('error');
				setMessage(`Unable to load the questionnaire. Please refresh the page and try again. ${error.message}`);
			});
	}, []);

	useEffect(() => {
		document.documentElement.classList.toggle('large-text', largeText);
		try {
			window.localStorage.setItem('bz-large-text', String(largeText));
		} catch {
			// The visual setting still works for this visit when storage is unavailable.
		}
		return () => document.documentElement.classList.remove('large-text');
	}, [largeText]);

	useEffect(() => {
		if (messageKind === 'confirmation') revealFeedback(messageRef.current);
	}, [message, messageKind]);

	function change(fieldId: string, value: JsonValue) {
		setAnswers((current) => ({ ...current, [fieldId]: value }));
		if (!intake) return;
		setMessageKind('status');
		setMessage('Saving...');
		saveChain.current = saveChain.current
			.catch(() => undefined)
			.then(async () => {
				const result = await api<{ rowVersion: number; updatedAt: string }>(
					'/api/client/answers',
					{
						method: 'PATCH',
						body: JSON.stringify({ rowVersion: versionRef.current, answers: { [fieldId]: value } }),
					},
					intake.csrfToken,
				);
				versionRef.current = result.rowVersion;
				setMessageKind('status');
				setMessage('Saved.');
			})
			.catch((error: Error) => {
				setMessageKind('error');
				setMessage(friendlySaveError(error));
				throw error;
			});
		void saveChain.current.catch(() => undefined);
		return saveChain.current;
	}

	async function finish() {
		if (!intake) return;
		setMessageKind('status');
		// Queue Finish as a write so later edits and repeated clicks wait for its version.
		saveChain.current = saveChain.current
			.then(async () => {
				const expectedVersion = versionRef.current;
				const result = await api<{ rowVersion: number }>('/api/client/finish', { method: 'POST', body: '{}' }, intake.csrfToken);
				// Do not adopt an unseen edit from another session along with our own version bump.
				if (result.rowVersion !== expectedVersion + 1) {
					throw new Error('This intake changed in another session. Refresh before saving again.');
				}
				versionRef.current = result.rowVersion;
				setMessageKind('confirmation');
				setMessage('Thank you. Your answers are saved for your Initial Consultation. You may return using your private link.');
			})
			.catch((error: unknown) => {
				setMessageKind('error');
				setMessage(friendlySaveError(error));
				throw error;
			});
		// The shared chain retains failures, but the button handler must not reject unhandled.
		await saveChain.current.catch(() => undefined);
	}

	if (!intake)
		return (
			<main id="main" className="centered-panel">
				<h1>{FIRM_NAME} Estate Planning Questionnaire</h1>
				<p role="status">{message}</p>
			</main>
		);
	return (
		<>
			<header className="app-header client-header">
				<div>
					<span className="eyebrow">{FIRM_NAME}</span>
					<h1>Estate Planning Questionnaire</h1>
					<p>Prepared for {intake.clientDisplayName}</p>
					<p className="office-phones">
						{FIRM_OFFICE_PHONES.map(({ office, phone }) => (
							<span key={office}>
								{office}: <a href={`tel:+1${phone.replaceAll('-', '')}`}>{phone}</a>
							</span>
						))}
					</p>
				</div>
				<button type="button" className="secondary" aria-pressed={largeText} onClick={() => setLargeText((value) => !value)}>
					{largeText ? 'Standard text' : 'Larger text'}
				</button>
			</header>
			<main id="main" className="page-shell">
				<div className="reassurance">
					<strong>You do not need to have an answer for every question.</strong> Please complete as much of the questionnaire as you can and
					leave any unanswered items blank. We will review any remaining questions during your Initial Consultation
				</div>
				<p
					ref={messageRef}
					className={`save-status${messageKind === 'error' ? ' save-status-error' : ''}${messageKind === 'confirmation' ? ' save-status-confirmation' : ''}`}
					role={messageKind === 'error' ? 'alert' : 'status'}
					aria-live={messageKind === 'error' ? 'assertive' : 'polite'}
					aria-atomic="true"
					tabIndex={messageKind === 'confirmation' ? -1 : undefined}
				>
					{message}
				</p>
				<QuestionnaireEditor answers={answers} onChange={change} onFinish={finish} showProgress={intake.progressMode === 'shown'} />
			</main>
		</>
	);
}
